import { useCallback, useMemo, useReducer, useEffect } from 'react';

import { useManualQuery } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';

import CheckDepositLoading from '#client/components/CheckDeposit/components/CheckDepositLoading';
import ExitConfirmModal from '#client/components/CheckDeposit/components/ExitConfirmModal';
import { CHECK_DEPOSIT_UNIT_STATUS } from '#client/components/CheckDeposit/constants/status';
import CheckDepositHistory from '#client/components/CheckDepositHistory';
import CheckDepositSubmissionOverview from '#client/components/CheckDepositSubmissionOverview';
import FormStepModal from '#client/components/FormStepModal';
import { TOKEN_TYPES } from '#client/constants/tokens';
import { CHECK_DEPOSIT_STATUS } from '#client/constants/transaction';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCheckDepositModal from '#client/hooks/useCheckDepositModal';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import { FormStepProvider } from '#client/hooks/useFormStep';
import { useMitekErrorHandler } from '#client/hooks/useMitekErrorHandler';
import { MitekSdkProvider } from '#client/hooks/useMitekSdk';
import useModalContext from '#client/hooks/useModalContext';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { GET_CHECK_DEPOSIT_FOR_RESUBMIT } from '#client/queries/checkDeposit';

import ConfirmationRejectReasons from './components/ConfirmationRejectReasons';
import ResubmitForm from './components/ResubmitForm';

import { RESUBMIT_ACTIONS } from './constants/actions';
import { RESUBMIT_STEPS } from './constants/steps';
import { initState, resubmitReducer } from './reducer/checkDepositResubmitReducer';

const listResubmitStatus = [
  CHECK_DEPOSIT_STATUS.AWAITING_IMAGES,
  CHECK_DEPOSIT_STATUS.AWAITING_FRONT_IMAGE,
  CHECK_DEPOSIT_STATUS.AWAITING_BACK_IMAGE,
  CHECK_DEPOSIT_STATUS.AWAITING_CUSTOMER_CONFIRMATION
];

export default function ResubmitModal({ handleClose, checkId, bypassReason = false }) {
  const {
    sizes: { isMobile }
  } = useBreakpoints();
  const [resubmitData, dispatchResubmitData] = useReducer(resubmitReducer, null, initState);
  const { clearAllModal, openModal } = useModalContext();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const { createCommonErrorToast, createErrorToast } = useToastContext();
  const captureException = useCaptureException();

  const { mitekErrorHandler } = useMitekErrorHandler();
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const { title, loading, currentFormStep, reason, amount, unitStatus, unitReason } = resubmitData;

  const [getCheckData] = useManualQuery(GET_CHECK_DEPOSIT_FOR_RESUBMIT, {
    noThrow: true
  });

  const handleCloseAndClearModals = useCallback(() => {
    handleClose();
    clearAllModal();
  }, [handleClose, clearAllModal]);

  const handleCloseModal = () => {
    if (currentFormStep === RESUBMIT_STEPS.RESUBMIT) {
      openModal({
        component: ExitConfirmModal,
        onOk: handleCloseAndClearModals,
        title: 'Are you sure?',
        description: 'If you leave now, you’ll lose your progress and have to start over.',
        okText: 'Yes, cancel',
        cancelText: ' No, keep going'
      });
    } else {
      handleCloseAndClearModals();
    }
  };

  const handleShowToastByUnitStatus = () => {
    const handleClick = () => {
      openModal({
        component: CheckDepositHistory
      });
    };

    switch (unitStatus) {
      case CHECK_DEPOSIT_UNIT_STATUS.REJECTED: {
        createErrorToast({
          title: `Check ID ${checkId} was rejected`,
          description: unitReason,
          secondaryAction: {
            label: 'Review',
            size: 'sm',
            onClick: handleClick
          }
        });
        break;
      }

      case CHECK_DEPOSIT_UNIT_STATUS.AWAITING_CUSTOMER_CONFIRMATION: {
        createErrorToast({
          title: `Check ID ${checkId} needs review`,
          description: unitReason,
          secondaryAction: {
            label: 'Review',
            size: 'sm',
            onClick: handleClick
          }
        });
        break;
      }

      default: {
        captureException(
          new Error(
            `Resubmit check deposit id: ${checkId} failed with Unit status: ${unitStatus}. Reason is ${unitReason}`
          ),
          {
            level: 'warning'
          }
        );
        createCommonErrorToast();
        break;
      }
    }
  };

  const loadCompletedHandler = () => {
    dispatchResubmitData({
      type: RESUBMIT_ACTIONS.STOP_LOADING
    });

    // if the current step is OVERVIEW, that mean the API submitted
    // and got the Rejected or AwaitingCustomerConfirmation status
    // so after the loading finish, it should navigate to the dashboard
    // and show the error toast
    if (currentFormStep === RESUBMIT_STEPS.RESUBMIT) {
      handleCloseAndClearModals();
      handleShowToastByUnitStatus();
    }
  };

  const openCheckDepositModal = useCheckDepositModal();

  const handleDepositAnother = useCallback(() => {
    handleCloseAndClearModals();
    openCheckDepositModal({ isResubmit: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCloseAndClearModals]);

  useEffect(() => {
    const getCheckDataAndHandleResponse = async () => {
      const customerToken = await getCustomerToken();

      if (!customerToken) {
        handleClose();
        return;
      }

      const { data, error } = await getCheckData({
        variables: {
          params: {
            identifier: {
              businessUuid: businessEntityUuid,
              unitAccountId
            },
            checkDepositId: checkId,
            customerToken
          }
        }
      });

      if (error || !data) {
        handleClose();
        createCommonErrorToast({ error });
        return;
      }

      const { checkDeposit } = data;

      // If check is not in resubmit status, close modal
      if (!listResubmitStatus.includes(checkDeposit?.status)) {
        handleClose();
      }

      dispatchResubmitData({
        type: RESUBMIT_ACTIONS.GO_TO_REASON,
        payload: {
          reason: checkDeposit.reason,
          status: checkDeposit.status,
          amount: checkDeposit.amount.amount,
          idempotencyKey: checkDeposit.idempotencyKey,
          checkId
        }
      });

      // Using in case the users open the resubmit modal via URL
      // we should redirect to the enter amount step instead show the reason step
      if (bypassReason) {
        dispatchResubmitData({ type: RESUBMIT_ACTIONS.GO_TO_RESUBMIT });
      }
    };

    if (checkId) {
      getCheckDataAndHandleResponse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkId]);

  const currentStep = useMemo(() => {
    switch (currentFormStep) {
      case RESUBMIT_STEPS.INIT:
        return (
          <Stack>
            <LinearProgress />
          </Stack>
        );
      case RESUBMIT_STEPS.REASON:
        return (
          <ConfirmationRejectReasons
            handleClose={handleCloseAndClearModals}
            reason={reason}
          />
        );
      case RESUBMIT_STEPS.RESUBMIT:
        return <ResubmitForm handleClose={handleCloseAndClearModals} />;
      case RESUBMIT_STEPS.OVERVIEW:
        return (
          <CheckDepositSubmissionOverview
            amount={amount}
            handleClose={handleCloseAndClearModals}
            handleDepositAnother={handleDepositAnother}
          />
        );
      default:
        captureException(new Error(`Unknown step: ${currentFormStep}`), {
          level: 'warning'
        });
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, currentFormStep, reason]);

  return (
    <MitekSdkProvider handleError={mitekErrorHandler}>
      <FormStepModal
        currentStep={currentFormStep}
        disableBack
        disableCloseModal={loading}
        handleClose={handleCloseModal}
        isHideBackButton
        title={title}
      >
        <FormStepProvider
          value={{
            dispatchResubmitData,
            resubmitData
          }}
        >
          {loading ? (
            <CheckDepositLoading
              height={isMobile ? '150px' : '250px'}
              loadCompletedHandler={loadCompletedHandler}
              text="Finalizing check deposit"
              url="/animation/lottie/money_Tetris_noText.json"
            />
          ) : (
            currentStep
          )}
        </FormStepProvider>
      </FormStepModal>
    </MitekSdkProvider>
  );
}

ResubmitModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  checkId: PropTypes.string.isRequired,
  bypassReason: PropTypes.bool
};
